import { range, sort } from "@prismicio/editor-support/Array"
import {
  BlankSlate,
  BlankSlateDescription,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  DefaultErrorMessage,
  Dialog,
  DialogContent,
  dialogErrorImage,
  ErrorBoundary,
  type FilterOption,
  FormInput,
  ScrollArea,
  Skeleton,
  Text,
  theme,
  Toast
} from "@prismicio/editor-ui"
import { Suspense, type TransitionStartFunction, useTransition } from "react"

import { documentStatus, getDocumentUrl } from "../../../core/document"
import { useEditorConfig } from "../../../EditorConfig"
import { DocumentCard } from "./DocumentCard"
import { useDocuments } from "./documentsData"
import { DocumentsListFilters } from "./Filters"
import { useFilters } from "./useFilters"

interface DocumentsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectDocument: (documentID: string) => void
  fixedCustomTypes?: Set<string>
  fixedTags?: Set<string>
}

export function DocumentsModal(props: DocumentsModalProps) {
  const { onSelectDocument, fixedCustomTypes, fixedTags, ...dialogProps } =
    props

  const config = useEditorConfig()

  const {
    searchTerm,
    filteredTags,
    setFilteredTags,
    filteredCustomTypes,
    setFilteredCustomTypes,
    filteredLocales,
    setFilteredLocales,
    typesFilter,
    tagsFilter
  } = useFilters(fixedCustomTypes, fixedTags)

  const [isLoadingDocuments, startTransition] = useTransition()

  return (
    <Dialog
      size={{ height: 727, width: 656 }}
      hasClose={false}
      {...dialogProps}
    >
      <DialogContent>
        <Box flexDirection="column" height="100%">
          <Box
            padding={{ top: 16, inline: 16, bottom: 8 }}
            flexDirection="column"
            gap={12}
          >
            <FormInput
              value={searchTerm.value}
              onValueChange={searchTerm.set}
              placeholder="Search a page"
              icon="search"
            />
            {config.searchDocuments && (
              <DocumentsListFilters
                filteredTags={filteredTags}
                setFilteredTags={setFilteredTags}
                filteredCustomTypes={filteredCustomTypes}
                setFilteredCustomTypes={setFilteredCustomTypes}
                filteredLocales={filteredLocales}
                setFilteredLocales={setFilteredLocales}
                fixedTags={fixedTags}
                fixedCustomTypes={fixedCustomTypes}
              />
            )}
            <Text variant="smallBold" color="grey11">
              Link to a document
            </Text>
          </Box>
          {config.searchDocuments ? (
            <ErrorBoundary renderError={DocumentsListError}>
              <Suspense fallback={<DocumentsListSkeleton />}>
                <DocumentsList
                  searchTerm={searchTerm.debouncedValue}
                  tagsFilter={tagsFilter}
                  typesFilter={typesFilter}
                  localesFilter={filteredLocales}
                  startTransition={startTransition}
                  onSelectDocument={onSelectDocument}
                />
              </Suspense>
            </ErrorBoundary>
          ) : (
            <EmptyList message="Slice Machine does not yet support mock data for linked documents in the simulator." />
          )}
          <LoadingToast isLoading={isLoadingDocuments} />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

interface LoadingToastProps {
  isLoading: boolean
}

function LoadingToast(props: LoadingToastProps) {
  const { isLoading } = props

  return (
    <Toast
      anchor={
        <Box
          position="relative"
          width="100%"
          justifyContent="center"
          bottom={72}
        />
      }
      loadingIndicator
      open={isLoading}
      title="Loading more results ..."
      seconds={1}
    />
  )
}

function DocumentsListError() {
  return (
    <Box
      height="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching documents."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}

interface DocumentsListProps {
  searchTerm: string
  tagsFilter: Set<FilterOption>
  typesFilter: Set<FilterOption>
  localesFilter: Set<FilterOption>
  startTransition: TransitionStartFunction
  onSelectDocument: (documentID: string) => void
}

function DocumentsList(props: DocumentsListProps) {
  const {
    searchTerm,
    tagsFilter,
    typesFilter,
    localesFilter,
    startTransition,
    onSelectDocument
  } = props

  const { documents, hasNextPage, fetchNextPage } = useDocuments({
    searchTerm,
    tagsFilter,
    localesFilter,
    typesFilter
  })

  const config = useEditorConfig()

  return (
    <ScrollArea
      onScrollToBottom={() => hasNextPage && startTransition(fetchNextPage)}
      style={{
        flexGrow: 1,
        paddingTop: theme.space[10],
        paddingBottom: theme.space[16],
        paddingInline: theme.space[16],
        display: "flex",
        flexDirection: "column",
        gap: theme.space[12]
      }}
    >
      {documents.length === 0 && <EmptyList />}

      {documents.map((document) => {
        const version = document.versions[0]
        if (!version) return null

        const documentUrl = getDocumentUrl({
          documentId: document.id,
          versionId:
            version.status === "archived" ? version.version_id : undefined,
          releaseId:
            version.status === "release" ? version.release_id : undefined,
          status: version.status,
          baseUrl: config.baseUrl
        })

        const sortedTags = sortTagsByFilteredTags({
          tags: version.tags,
          tagsFilter
        })

        return (
          <DocumentCard
            key={`${document.id}-${version.status}`}
            documentType={version.custom_type_label}
            documentUrl={documentUrl}
            description={version.preview_summary}
            tags={sortedTags}
            title={document.title}
            previewImageUrl={version.preview_image}
            onSelect={() => onSelectDocument(document.id)}
            statusColor={documentStatus[version.status].color}
          />
        )
      })}
    </ScrollArea>
  )
}

interface EmptyListProps {
  message?: string
}

function EmptyList(props: EmptyListProps) {
  const { message = "We can't find any versions matching this request" } = props

  const title = "Oops!"

  return (
    <Box justifyContent="center" flexDirection="column" height="100%">
      <BlankSlate>
        <BlankSlateIcon
          name="close"
          lineColor="purple10"
          backgroundColor="purple3"
          borderColor="purple6"
          borderStyle="dashed"
          size="xlarge"
        />
        <BlankSlateTitle size="big">{title}</BlankSlateTitle>
        <BlankSlateDescription width={360}>{message}</BlankSlateDescription>
      </BlankSlate>
    </Box>
  )
}

function DocumentsListSkeleton() {
  // Note: Each Skeleton's height is roughly the average of the Card's height with and without its footer.
  return (
    <Box
      flexDirection="column"
      gap={12}
      padding={{ top: 10, inline: 16, bottom: 16 }}
    >
      {range(1, 5).map((key) => (
        <Skeleton key={key} height={150} />
      ))}
    </Box>
  )
}

interface SortTagsByFilteredTagsArgs {
  tagsFilter: Set<FilterOption>
  tags: string[]
}

export function sortTagsByFilteredTags(args: SortTagsByFilteredTagsArgs) {
  const { tags, tagsFilter } = args
  // Get filters by last added to map through
  const filterLabels = [...tagsFilter].map((tag) => tag.label).reverse()
  const sortedTags = sort(tags, (tag) => {
    // Get the tags in descending order
    return -filterLabels.indexOf(tag)
  })

  return sortedTags
}
